// BRIEF 105 §1b — THE DRAFT THE ENROLMENT INVITATION OPENS.
//
// §1b-i: "issuing an invitation opens a draft to that collaborator, prefilled
//  with the link. The screen still shows the link — the draft is the
//  convenience, not a replacement, and an admin who prefers to read it down the
//  phone must still be able to."
//
// So this file builds the SUBJECT and BODY and nothing else. It opens no
// window, reads no preference and knows no collaborator: `AuthIncidentPanel`
// hands it a name, a link, an expiry and a resolver, and hands the result to
// `openCompose` (src/lib/outlookCompose.ts:41), which is the machinery the
// parties pane already uses and which routes to the outlook deeplink / Gmail /
// `mailto:` per the reader's own `emailClient` preference.
//
// ── §1b-ii — EVERY LANGUAGE THE FIRM HAS, AND NOT A LIST OF THREE ───────────
//
// RULED: there is no per-collaborator language and none is being added. So the
// body carries the instructions in all of them, one after another, and the SET
// arrives as an argument. Its one source is `useTranslation().supported` —
// which IS `SUPPORTED_LANGS` at `src/i18n.tsx:17`, the single place this
// application declares what languages it has, and the same set
// `translations_seed.json` carries rows for (4,499 keys × en/fr/nl, counted).
// A fourth language is one edit to that line and this draft picks it up with
// nothing to change here. A hard-coded `["en","fr","nl"]` in this file would
// have been a second declaration of the same fact, which is the coordinate that
// rots.
//
// ── §1b-iii — AND THE BODY MUST NOT MAKE THE LINK UNUSABLE ──────────────────
//
// The classic failure is a long URL wrapped, truncated or split by a client.
// Three properties are load-bearing and all three are ASSERTED by
// `scripts/check-invite-compose.js`, which transpiles this file and
// `outlookCompose.ts` with the real TypeScript compiler and reads the link back
// out of the URL that would actually be handed over:
//
//   1. THE LINK IS ALONE ON ITS OWN LINE, first, with a blank line either side.
//      Nothing precedes it, nothing follows it, and it ends the line — so there
//      is no trailing full stop for an autolinker to swallow and no sentence
//      for a client to reflow around.
//   2. THE BODY CONTAINS NO `+`. `encodeURIComponent` leaves `+` alone, and a
//      client that decodes the query with form-decoding rules turns it into a
//      space. The token's alphabet is base64URL (`enrolmentInvitation.js:406`:
//      43 chars of `[A-Za-z0-9_-]`) so the LINK can never carry one; this file
//      must not put one in the prose either, and the check asserts it.
//   3. THE WHOLE COMPOSE URL FITS. The check prints the byte length of all
//      three schemes and fails past a stated bound.
//
// ── WHAT IT DOES NOT DO ─────────────────────────────────────────────────────
//
// It does not send anything, and it does not put the link anywhere the screen
// has not already put it. `RULINGS.md` 2026-08-19 states the consequence: the
// app drafts the invitation and still does not send it, and the mitigation for
// a link that now travels through a mailbox is the WINDOW — the hours setting
// 103 built — not the channel.

/** The one separator between two language blocks. Plain text, no markup. */
const LANG_SEPARATOR = "— — — — — — — — — — — — — — — — — — — —";

export type InviteMailInput = {
  /** The collaborator's display name, for the greeting. */
  name: string;
  /** The one-time link, exactly as the screen shows it. */
  url: string;
  /** Already-formatted expiry instant, one string per language is overkill. */
  expiresAtLabel: string;
  /** The firm's languages — `useTranslation().supported`, in its own order. */
  langs: readonly string[];
  /** `useTranslation().tIn` — resolve a key in a NAMED language. */
  tIn: (lang: string, key: string, vars?: Record<string, unknown>) => string;
};

export type InviteMail = { subject: string; body: string };

/**
 * §1b-ii — one short block per language, in the firm's own order.
 *
 * Two sentences, deliberately: what this is and what to do, then when it dies.
 * A longer block multiplies by the number of languages and is what pushes a
 * `mailto:` past what a desktop handler will accept.
 */
function block(
  lang: string,
  { name, expiresAtLabel, tIn }: InviteMailInput,
): string {
  const greeting = tIn(lang, "invite.mail.greeting", {
    who: name,
    fallback: `${name},`,
  });
  const what = tIn(lang, "invite.mail.what", {
    fallback:
      "The link above sets up your sign-in. It works once, and only for you — do not pass it on.",
  });
  const when = tIn(lang, "invite.mail.when", {
    d: expiresAtLabel,
    fallback: `It stops working on ${expiresAtLabel}. If it has expired, ask for another one.`,
  });
  return `${greeting}\n${what}\n${when}`;
}

/**
 * §1b-i/§1b-ii/§1b-iii — the draft, whole.
 *
 * The link goes FIRST and alone. A reader in any of the firm's languages finds
 * it before the prose rather than after two blocks written for somebody else,
 * and a client reflowing the paragraphs cannot reach it.
 */
export function buildInviteMail(input: InviteMailInput): InviteMail {
  const langs = input.langs && input.langs.length ? input.langs : ["en"];

  // The subject carries every language too, joined — it is the first thing
  // read, and there is no reader to pick for. Short by construction.
  const subject = langs
    .map((l) =>
      input.tIn(l, "invite.mail.subject", {
        fallback: "Your sign-in link",
      }),
    )
    .join(" · ");

  const body =
    `\n${input.url}\n\n` +
    langs.map((l) => block(l, input)).join(`\n\n${LANG_SEPARATOR}\n\n`) +
    "\n";

  return { subject, body };
}

/**
 * §1b-iii's three properties, as a function rather than as a paragraph, so the
 * check script and any later reader ask the same question of the same string.
 * Returns the list of violations; empty means the body is safe to hand over.
 */
export function inviteMailFaults(mail: InviteMail, url: string): string[] {
  const faults: string[] = [];
  const lines = mail.body.split("\n");
  const own = lines.filter((l) => l.trim() === url.trim());
  if (own.length !== 1)
    faults.push(
      `the link must appear on exactly one line of its own; found ${own.length}`,
    );
  const idx = lines.findIndex((l) => l.trim() === url.trim());
  if (idx >= 0 && lines[idx] !== url)
    faults.push("the link's line carries something besides the link");
  if (mail.body.includes(url + ".") || mail.body.includes(url + ","))
    faults.push("the link is followed by punctuation an autolinker would eat");
  if (mail.body.includes("+"))
    faults.push(
      "the body contains a '+', which a form-decoding client turns into a space",
    );
  if (mail.subject.includes("+"))
    faults.push("the subject contains a '+', same reason");
  return faults;
}
